import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["accessibilityLabelClearButton", "action", "autoComplete", "disabled", "error", "iconLeft", "id", "label", "labelHidden", "loading", "message", "name", "onBlur", "onChange", "onFocus", "onInputChange", "onSelect", "optionalLabel", "placeholder", "placement", "readOnly", "suggestions", "type", "value", "forceSelection"];
import React, { forwardRef, useState } from 'react';
import cx from 'classnames';
import Downshift from 'downshift';
import { usePopper } from 'react-popper';
import TextField, { createMessageId } from '../TextField';
import system from '../../system';
import Message from '../Message';
import Box from '../Box';
import { escapeRegexCharacters } from '../../utils';
import Suggestion from './Suggestion';
import SuggestionContainer from './SuggestionContainer';
import styles from './index.module.scss';
var SYSTEM_PROPS = ['marginProps', 'floatProps'];
var AutoComplete = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var accessibilityLabelClearButton = _ref.accessibilityLabelClearButton,
      action = _ref.action,
      autoComplete = _ref.autoComplete,
      disabled = _ref.disabled,
      error = _ref.error,
      iconLeft = _ref.iconLeft,
      id = _ref.id,
      label = _ref.label,
      labelHidden = _ref.labelHidden,
      loading = _ref.loading,
      message = _ref.message,
      name = _ref.name,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      onInputChange = _ref.onInputChange,
      onSelect = _ref.onSelect,
      optionalLabel = _ref.optionalLabel,
      placeholder = _ref.placeholder,
      placement = _ref.placement,
      readOnly = _ref.readOnly,
      suggestions = _ref.suggestions,
      type = _ref.type,
      value = _ref.value,
      forceSelection = _ref.forceSelection,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _React$useState = React.useState([]),
      internalSuggestions = _React$useState[0],
      setInternalSuggestions = _React$useState[1];

  var _useState = useState(null),
      referenceElement = _useState[0],
      setReferenceElement = _useState[1];

  var _useState2 = useState(null),
      popperElement = _useState2[0],
      setPopperElement = _useState2[1];

  var _system = system(props, SYSTEM_PROPS),
      className = _system[0],
      style = _system[1],
      rest = _system[2];

  var isExternal = onInputChange && typeof onInputChange === 'function';
  var messageId = createMessageId(id);
  var popper = usePopper(referenceElement, popperElement, {
    placement: placement
  }); // Update internal suggestions when passed from external

  React.useEffect(function () {
    if (isExternal) {
      setInternalSuggestions(suggestions);
    }
  }, [suggestions, internalSuggestions]); // Filter the internalSuggestions by the value

  var getSuggestions = function getSuggestions(inputValue) {
    var stringValue = typeof inputValue === 'string' ? inputValue : '';
    var escapedValue = escapeRegexCharacters(stringValue.trim());

    if (escapedValue === '') {
      return [];
    }

    var regex = new RegExp("^" + escapedValue, 'i');
    return suggestions.filter(function (item) {
      return regex.test(item);
    });
  }; // DownShift will call this function every time you need to update internalSuggestions.


  var handleInputValueChange = function handleInputValueChange(inputValue) {
    // Only update internal values is `onInputChange` not controlled
    if (isExternal) {
      onInputChange(inputValue);
    } else {
      setInternalSuggestions(getSuggestions(inputValue));
    }
  };

  var handleStateChange = function handleStateChange(changes, stateAndHelpers) {
    // eslint-disable-next-line
    if (changes.hasOwnProperty('selectedItem')) {
      // eslint-disable-next-line
      onChange(changes.selectedItem || '', stateAndHelpers); // eslint-disable-next-line
    } else if (changes.hasOwnProperty('inputValue')) {
      // eslint-disable-next-line
      onChange(changes.inputValue, stateAndHelpers);
    }
  }; // Temporary fallback for previous Autosuggest implementation


  var handleSelect = function handleSelect(selectedItem, stateAndHelpers) {
    // TODO: Remove this part
    if (props.onSuggestionSelected) {
      // eslint-disable-next-line
      console.warn('[DEPRECATED](Autocomplete): "onSuggestionSelected" is deprecated. Please use onSelect instead.');
      props.onSuggestionSelected({
        // eslint-disable-next-line
        preventDefault: function preventDefault() {},
        target: {
          value: selectedItem || ''
        }
      }, {
        suggestion: selectedItem || ''
      });
    } // Native downshift


    if (onSelect) {
      onSelect(selectedItem, stateAndHelpers);
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    id: "autocomplete-" + id,
    className: cx(styles.root, popper.state && popper.state.placement && popper.state.placement.includes('top') && styles.top, className),
    style: style
  }, /*#__PURE__*/React.createElement(Downshift, _extends({}, rest, {
    selectedItem: forceSelection ? undefined : value,
    id: id,
    onStateChange: handleStateChange,
    onInputValueChange: handleInputValueChange,
    onSelect: handleSelect
  }), function (_ref2) {
    var getInputProps = _ref2.getInputProps,
        getMenuProps = _ref2.getMenuProps,
        getItemProps = _ref2.getItemProps,
        getLabelProps = _ref2.getLabelProps,
        selectedItem = _ref2.selectedItem,
        isOpen = _ref2.isOpen,
        highlightedIndex = _ref2.highlightedIndex,
        clearSelection = _ref2.clearSelection;
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      ref: setReferenceElement
    }, /*#__PURE__*/React.createElement(TextField, getInputProps({
      clearButton: true,
      disabled: disabled,
      error: error,
      iconLeft: iconLeft,
      label: label,
      labelHidden: labelHidden,
      name: name,
      onBlur: onBlur,
      onFocus: onFocus,
      placeholder: placeholder,
      ref: ref,
      type: type,
      action: action,
      readOnly: readOnly,
      optionalLabel: optionalLabel,
      message: undefined,
      // Extract message from the component
      messageId: message ? messageId : undefined,
      // Pass the generated messageId here
      labelProps: getLabelProps(),
      onRequestClear: clearSelection,
      accessibilityLabelClearButton: accessibilityLabelClearButton,
      autoComplete: autoComplete
    }))), /*#__PURE__*/React.createElement(SuggestionContainer, getMenuProps({
      isOpen: isOpen && internalSuggestions.length !== 0,
      loading: loading
    }), /*#__PURE__*/React.createElement("ul", {
      className: styles.list
    }, isOpen ? internalSuggestions.map(function (item, index) {
      return /*#__PURE__*/React.createElement(Suggestion, _extends({
        key: item
      }, getItemProps({
        item: item,
        index: index,
        highlighted: selectedItem === item || highlightedIndex === index
      })), item);
    }) : null)), /*#__PURE__*/React.createElement(Box, {
      ref: setPopperElement,
      className: styles.containerRef,
      position: "absolute",
      "aria-hidden": "true"
    }));
  }), message && /*#__PURE__*/React.createElement(Message, {
    mt: 2,
    error: error,
    id: messageId
  }, message));
});
AutoComplete.displayName = 'AutoComplete';
AutoComplete.identifier = AutoComplete.displayName; // used in docs website

AutoComplete.clonableProps = SYSTEM_PROPS;
AutoComplete.defaultProps = {
  accessibilityLabelClearButton: 'clear selection',
  type: 'text',
  placement: 'bottom',
  autoComplete: 'off'
};
export default AutoComplete;